/**
 * ゴミ箱に入っているキャンバスへの編集を拒否するときの共通文言。
 * サーバの拒否理由、クライアントのお知らせ、常設バナーで同じ文言を使う。
 */
export const CANVAS_TRASHED_EDIT_MESSAGE =
  "このキャンバスはゴミ箱に入っています。編集する場合はゴミ箱から戻してください。";

/**
 * 完全削除の確認入力で求める文字列。確認ダイアログの入力欄のラベルと placeholder に示し、
 * 手入力がこの文字列と完全に一致したときだけ削除ボタンを有効にする。サーバも要求の confirmation を
 * 同じ文字列との完全一致で照合する（前後の空白を落とさない・部分一致を認めない）。
 */
export const CANVAS_PERMANENT_DELETE_CONFIRMATION_TEXT = "完全に削除";

/** 最後にゴミ箱へ入れてから、自動で完全削除するまでの日数。 */
export const CANVAS_TRASH_RETENTION_DAYS = 90;

/** 自動の完全削除までの保持期間（90×24 時間）。期限はサーバ時刻（UTC）で数える。 */
export const CANVAS_TRASH_RETENTION_MS = CANVAS_TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;

/** 完全削除の処理中のキャンバスを開こう・編集しようとしたときの共通文言。 */
export const CANVAS_PURGE_IN_PROGRESS_MESSAGE =
  "このキャンバスは完全に削除する処理中のため、開くことも編集することもできません。";

/** 完全削除が済んだキャンバスを開こう・編集しようとしたときの共通文言。 */
export const CANVAS_PURGED_MESSAGE = "このキャンバスは完全に削除されました。";

/**
 * 完全削除で消す S3 の prefix（キャンバス ID の直前まで。添付・サムネイル・全体像 PDF とその一時置き場）。
 * API（削除の worker）と CDK（IAM の対象・作成通知の対象）が同じ値を使う。CDK はビルドの構成上このパッケージを
 * 読み込めないため値を複製し、CDK のテストがこの定数との一致を確かめる。
 */
export const CANVAS_PURGE_S3_PREFIXES = [
  "pending",
  "attachments",
  "thumbnails",
  "exports",
  "exports-staging",
] as const;

/** CloudFront から公開配信している prefix（完全削除の無効化の対象。WebStack の配信の振り分けと一致させる）。 */
export const CANVAS_PURGE_CDN_PREFIXES = ["attachments", "pending", "thumbnails"] as const;

/** 期限探索と完全削除の worker が出す埋め込みメトリクスの名前空間（CDK のアラームと一致させる）。 */
export const CANVAS_RETENTION_METRIC_NAMESPACE = "RefinementCanvas/Retention";

/**
 * 削除キュー（SQS）の可視性タイムアウト（秒）。失敗したメッセージは、この時間の後に再配信される。
 * worker の上限時間（2 分）の 6 倍（AWS の推奨）で、CDK のキューの設定と一致させる。worker は失敗した処理の
 * 続きの予定時刻をこの時間の後に置き、期限探索が再配信と重ねて投入しないようにする。
 */
export const CANVAS_PURGE_QUEUE_VISIBILITY_TIMEOUT_SECONDS = 12 * 60;

/**
 * 削除キュー（SQS）の最大受信回数。これを越えたメッセージは DLQ へ移る。worker の失敗の記録の上限も同じ値にし、
 * 上限に達した処理を期限探索が再投入しない（DLQ と運用の再投入に任せる）こととそろえる。CDK のキューの設定と一致させる。
 */
export const CANVAS_PURGE_MAX_RECEIVE_COUNT = 5;

/**
 * 完全削除の状態（GET /api/canvases/:canvasId/purge-status の応答）。
 * - purging: 削除の処理中
 * - retrying: 処理中に失敗し、再試行を待っている（完了ではない）
 * - purged: 削除が済んだ
 */
export type CanvasPurgeStatus = "purging" | "retrying" | "purged";

/**
 * 確認入力が完全削除の確認文字列と完全に一致するか。前後の空白の除去・部分一致・大文字小文字の
 * 同一視は行わない（入力欄の placeholder を代わりに使うこともしない）。
 */
export function isCanvasPermanentDeleteConfirmed(input: string): boolean {
  return input === CANVAS_PERMANENT_DELETE_CONFIRMATION_TEXT;
}

/** サーバが記録するゴミ箱移動日時の形式（Date#toISOString の出力）。 */
const TRASHED_AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u;

/**
 * ゴミ箱へ移動した日時（ISO 8601 の UTC）を、エポックミリ秒へ変換する。
 * サーバが Date#toISOString で記録した形式だけを受け付け、それ以外（空・別形式・存在しない日付）は
 * undefined を返す。呼び出し側は不正な日時から期限を推測したり、現在時刻へ置き換えたりしない。
 */
export function parseCanvasTrashedAt(trashedAt: string | undefined): number | undefined {
  if (trashedAt === undefined || !TRASHED_AT_PATTERN.test(trashedAt)) return undefined;
  const epochMs = Date.parse(trashedAt);
  if (!Number.isFinite(epochMs)) return undefined;
  // 2 月 30 日のように、形式は正しくても存在しない日付は丸められた別の日時になるため除外する。
  if (new Date(epochMs).toISOString() !== trashedAt) return undefined;
  return epochMs;
}

/**
 * 自動の完全削除の期限（エポックミリ秒）。最後にゴミ箱へ入れた日時から 90×24 時間後。
 * 日時が不正なら undefined（自動削除の対象にしない）。
 */
export function canvasTrashRetentionDueAtMs(trashedAt: string | undefined): number | undefined {
  const trashedAtMs = parseCanvasTrashedAt(trashedAt);
  return trashedAtMs === undefined ? undefined : trashedAtMs + CANVAS_TRASH_RETENTION_MS;
}

/**
 * 自動の完全削除の期限に達しているか。期限ちょうどは対象、その 1 ミリ秒前は対象外。
 * 日時が不正なら false（自動削除の対象にしない）。nowMs はサーバ時刻のエポックミリ秒。
 */
export function isCanvasTrashRetentionExpired(
  trashedAt: string | undefined,
  nowMs: number
): boolean {
  const dueAtMs = canvasTrashRetentionDueAtMs(trashedAt);
  return dueAtMs !== undefined && nowMs >= dueAtMs;
}
